(function () {
  if (customElements.get("slib-cart-page")) return;

  class SlibCartPage extends HTMLElement {
    constructor() {
      super();
      this.pendingQuantityTimer = null;
    }

    connectedCallback() {
      this.addEventListener("click", this.onClick.bind(this));
      this.addEventListener("change", this.onChange.bind(this));
    }

    onClick(event) {
      const quantityButton = event.target.closest("[data-slib-cart-quantity-button]");
      if (quantityButton) {
        event.preventDefault();
        this.onQuantityButton(quantityButton);
        return;
      }

      const removeButton = event.target.closest("[data-slib-cart-remove]");
      if (removeButton) {
        event.preventDefault();
        const line = Number(removeButton.dataset.line);
        if (line) this.changeLine(line, 0);
        return;
      }

      const discountButton = event.target.closest("[data-slib-cart-discount-apply]");
      if (discountButton) {
        event.preventDefault();
        this.applyDiscount(discountButton);
      }
    }

    onChange(event) {
      const variantSelect = event.target.closest("[data-slib-cart-line-variant-select]");
      if (variantSelect) {
        this.changeLineVariant(Number(variantSelect.dataset.index), variantSelect.value);
        return;
      }

      const input = event.target.closest("[data-slib-cart-quantity-input]");
      if (!input) return;
      if (input.dataset.slibCartBundleLine === "true") return;

      const line = Number(input.dataset.index);
      const quantity = this.normalizeQuantity(input, Number(input.value));
      input.value = quantity;

      window.clearTimeout(this.pendingQuantityTimer);
      this.pendingQuantityTimer = window.setTimeout(() => {
        this.changeLine(line, quantity);
      }, 250);
    }

    onQuantityButton(button) {
      const row = button.closest("[data-slib-cart-line]");
      const input = row && row.querySelector("[data-slib-cart-quantity-input]");
      if (!row || !input) return;

      const line = Number(row.dataset.slibCartLine);
      const current = Number(input.value) || 0;
      const step = Number(button.dataset.step) || 0;
      const quantity = this.normalizeQuantity(input, current + step);

      if (input.dataset.slibCartBundleLine === "true") {
        if (step > 0) {
          this.duplicateBundleLine(line);
        } else {
          this.changeLine(line, 0);
        }
        return;
      }

      input.value = quantity;
      this.changeLine(line, quantity);
    }

    normalizeQuantity(input, quantity) {
      const min = Number(input.getAttribute("min")) || 0;
      const max = input.hasAttribute("max") ? Number(input.getAttribute("max")) : null;
      let nextQuantity = Number.isFinite(quantity) ? quantity : min;

      nextQuantity = Math.max(min, nextQuantity);
      if (max !== null && Number.isFinite(max)) nextQuantity = Math.min(max, nextQuantity);

      return nextQuantity;
    }

    async changeLine(line, quantity) {
      if (!line && line !== 0) return;

      this.setLoading(true);
      this.clearErrors();

      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ line, quantity }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => null);
          throw new Error((error && (error.description || error.message)) || "Cart update failed.");
        }

        await this.refreshSection();
      } catch (error) {
        this.setError(error.message || "Cart update failed.");
        this.setLoading(false);
      }
    }

    async changeLineVariant(line, variantId) {
      if (!line || !variantId) return;

      this.setLoading(true);
      this.clearErrors();

      try {
        const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((response) => response.json());
        const item = cart.items[line - 1];
        const properties = item && item.properties ? item.properties : {};

        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            line,
            id: Number(variantId),
            quantity: item ? Number(item.quantity || 1) : 1,
            properties,
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => null);
          throw new Error((error && (error.description || error.message)) || "Cart update failed.");
        }

        await this.refreshSection();
      } catch (error) {
        this.setError(error.message || "Cart update failed.");
        this.setLoading(false);
      }
    }

    async duplicateBundleLine(line) {
      if (!line) return;

      this.setLoading(true);
      this.clearErrors();

      try {
        const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((response) => response.json());
        const item = cart.items[line - 1];
        if (!item) throw new Error("Cart update failed.");

        const properties = { ...(item.properties || {}) };
        const bundleId = properties._bundle_id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const slots = cart.items
          .filter((cartItem) => cartItem.properties && cartItem.properties._bundle_id === bundleId)
          .map((cartItem) => cartItem.properties && cartItem.properties._bundle_slot)
          .map((slot) => Number(slot || 0))
          .filter((slot) => slot > 0);

        properties._bundle_id = bundleId;
        properties._bundle_slot = String((slots.length ? Math.max(...slots) : 0) + 1);

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: item.variant_id,
                quantity: 1,
                properties,
              },
            ],
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => null);
          throw new Error((error && (error.description || error.message)) || "Cart update failed.");
        }

        await this.refreshSection();
      } catch (error) {
        this.setError(error.message || "Cart update failed.");
        this.setLoading(false);
      }
    }

    async refreshSection() {
      const sectionId = this.dataset.sectionId;
      const requestPath = window.location.pathname || "/cart";
      const response = await fetch(`${requestPath}?section_id=${encodeURIComponent(sectionId)}`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) throw new Error("Cart refresh failed.");

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("slib-cart-page");

      if (!fresh) throw new Error("Cart refresh failed.");

      this.innerHTML = fresh.innerHTML;
      this.className = fresh.className;
      this.setAttribute("style", fresh.getAttribute("style") || "");
      this.removeAttribute("aria-busy");
      this.classList.remove("is-updating");
      this.updateCartCount();
    }

    async updateCartCount() {
      try {
        const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((response) => response.json());
        document.querySelectorAll(".slib-header__cart-count").forEach((count) => {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        });
      } catch (error) {
        // Cart contents were already refreshed; header count sync is best-effort.
      }
    }

    applyDiscount(button) {
      const wrapper = button.closest(".slib-cart-discount");
      const input = wrapper && wrapper.querySelector("[data-slib-cart-discount-input]");
      const error = wrapper && wrapper.querySelector("[data-slib-cart-discount-error]");
      const code = input ? input.value.trim() : "";

      if (!code) {
        if (error) error.hidden = false;
        if (input) input.focus();
        return;
      }

      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
    }

    setLoading(isLoading) {
      this.classList.toggle("is-updating", isLoading);
      if (isLoading) {
        this.setAttribute("aria-busy", "true");
      } else {
        this.removeAttribute("aria-busy");
      }
    }

    clearErrors() {
      this.querySelectorAll("[data-slib-cart-line-error], [data-slib-cart-errors]").forEach((element) => {
        element.textContent = "";
        element.hidden = true;
      });
    }

    setError(message) {
      const errorTarget = this.querySelector("[data-slib-cart-errors]") || this.querySelector("[data-slib-cart-live-region]");
      if (errorTarget) {
        errorTarget.textContent = message;
        errorTarget.hidden = false;
      }
    }
  }

  customElements.define("slib-cart-page", SlibCartPage);
})();
